import {
  OperationVariables,
  SuspenseQueryHookOptions,
  UseSuspenseQueryResult,
} from '@apollo/client';

import { GraphQLResult, getError, getTypeOrNull } from './apollo';

type QueryVariables = {
  name: string;
};

type GraphQLErrorShape = {
  __typename: 'Error';
  message?: string | null;
};

type SuspenseQueryHook<
  TData,
  TVariables extends OperationVariables = QueryVariables,
> = (
  options?: SuspenseQueryHookOptions<TData | undefined, TVariables>,
) => UseSuspenseQueryResult<TData | undefined, TVariables>;

export type StandardGetSuspenseHookOptions = {
  name: string;
  fetchPolicy?: SuspenseQueryHookOptions['fetchPolicy'];
};

type StandardGetSuspenseHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem;
};

export function withStandardGetSuspenseHook<
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  useSuspenseQuery: SuspenseQueryHook<TData, TVariables>,
  dataPath: TPath,
  queryOptions?: Pick<SuspenseQueryHookOptions, 'fetchPolicy'>,
): (
  options: StandardGetSuspenseHookOptions,
) => StandardGetSuspenseHookResult<TItem, TPath>;

export function withStandardGetSuspenseHook<
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  useSuspenseQuery: SuspenseQueryHook<TData, TVariables>,
  dataPath: TPath,
  queryOptions?: Pick<SuspenseQueryHookOptions, 'fetchPolicy'>,
) {
  return (
    options: StandardGetSuspenseHookOptions,
  ): StandardGetSuspenseHookResult<TItem, TPath> => {
    const { data, error: networkError } = useSuspenseQuery({
      variables: { name: options.name } as TVariables,
      fetchPolicy: queryOptions?.fetchPolicy ?? options.fetchPolicy,
    });

    const typedData =
      getTypeOrNull(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;
    const appError =
      getError(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;
    const error = appError ?? networkError;

    if (error) {
      throw error;
    }

    return {
      [dataPath]: typedData as TItem,
    } as StandardGetSuspenseHookResult<TItem, TPath>;
  };
}
